import React, { useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useAtom } from 'jotai';
import axios from 'axios';
import config from '../Config';
import { authUserAtom, userRoleAtom } from '../Services/store/atoms';
import { PiSealWarningFill } from 'react-icons/pi';
import { FaArrowRight } from 'react-icons/fa';

// Aviso obligatorio: si el admin le envio sugerencias a un pack (status 'appeal') y la
// modelo entra a la app sin haber tocado el email/push/notificacion, esto se lo muestra
// de una igual, sin boton para cerrarlo — la unica salida es ir a editar el pack.
//
// Debe montarse DENTRO del <Router> (ver Navigator.jsx) para poder navegar con
// history.push. Antes navegaba con window.location.href (recarga completa de la
// pagina): eso reiniciaba la app justo cuando los atoms de auth (persistidos en
// localStorage) todavia no terminaban de rehidratarse, asi que RouteCheck la
// mandaba a "/" y de ahi de vuelta a "/model" — perdiendo el deep-link y quedando
// atrapada en el mismo lugar con el modal reapareciendo.
function PendingPackSuggestionsModal() {
    const [authUser] = useAtom(authUserAtom);
    const [userRole] = useAtom(userRoleAtom);
    const history = useHistory();
    const location = useLocation();
    const [packs, setPacks] = useState([]);
    const [checked, setChecked] = useState(false);
    // Una vez que toca "Editar pack" no se vuelve a mostrar por el resto de la sesion
    // (aunque navegue de un lado a otro sin terminar de reenviarlo) — recien se vuelve a
    // evaluar si cierra y vuelve a abrir la pagina, que reinicia este estado.
    const [dismissed, setDismissed] = useState(false);

    // No molestar si ya esta en la pantalla de edicion de un pack.
    const onEditScreen = location.pathname.startsWith('/model/packs/edit/');

    // Se vuelve a consultar en cada cambio de ruta (no solo una vez al entrar a la app):
    // asi, apenas la modelo reenvia un pack (por cualquier camino) y vuelve a "Mis Packs",
    // el pack ya resuelto (status ya no 'appeal') sale de la lista y el modal deja de
    // aparecer — solo reaparece si el admin le manda una sugerencia nueva sobre otro pack.
    useEffect(() => {
        if (!authUser?.token || userRole !== 'model' || onEditScreen || dismissed) return;

        axios.get(`${config.REACT_APP_BASE_URL}/api/dasboard/v1/model/packs/pending-suggestions`, {
            headers: { Authorization: `Bearer ${authUser.token}` },
        })
            .then((response) => setPacks(response.data || []))
            .catch(() => setPacks([]))
            .finally(() => setChecked(true));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [authUser, userRole, location.pathname, dismissed]);

    const goEditPack = (packId) => {
        setDismissed(true);
        history.push(`/model/packs/edit/${packId}`);
    };

    if (!checked || packs.length === 0 || onEditScreen || dismissed) return null;

    return (
        <div
            className="fixed inset-0 flex items-center justify-center px-4 py-8 overflow-y-auto"
            style={{ zIndex: 100000 }}
        >
            <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" />
            <div className="relative w-full max-w-md rounded-2xl shadow-2xl overflow-hidden bg-white dark:bg-gray-900">
                <div className="bg-gradient-to-r from-amber-500 to-orange-500 px-5 py-4 flex items-center gap-2.5">
                    <PiSealWarningFill className="text-white text-2xl flex-shrink-0" />
                    <div>
                        <p className="text-white font-semibold text-base leading-tight">
                            {packs.length > 1 ? 'Tienes packs con sugerencias' : 'Tienes un pack con sugerencias'}
                        </p>
                        <p className="text-white/80 text-xs">El equipo te pide algunos cambios antes de aprobarlo</p>
                    </div>
                </div>
                <div className="px-4 sm:px-5 py-4 flex flex-col gap-3 max-h-[60vh] overflow-y-auto">
                    {packs.map((pack) => (
                        <div key={pack.pack_id} className="rounded-xl border border-gray-200 dark:border-gray-700 p-3 flex flex-col gap-3">
                            <div className="flex items-start gap-3">
                                {pack.cover ? (
                                    <img src={pack.cover} alt="" className="w-14 h-14 rounded-xl object-cover flex-shrink-0" />
                                ) : (
                                    <div className="w-14 h-14 rounded-xl bg-gray-200 dark:bg-gray-700 flex-shrink-0" />
                                )}
                                <p className="flex-1 min-w-0 text-xs text-gray-600 dark:text-gray-300 leading-relaxed line-clamp-3">
                                    {pack.admin_response}
                                </p>
                            </div>
                            <button
                                onClick={() => goEditPack(pack.pack_id)}
                                className="w-full flex items-center justify-center gap-1.5 bg-rose-500 hover:bg-rose-600 text-white text-xs font-semibold px-3 py-2.5 rounded-full transition-colors"
                            >
                                Editar pack <FaArrowRight className="text-[10px]" />
                            </button>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}

export default PendingPackSuggestionsModal;
